<template>
  <div class="custom-states-panel">
    <span class="title">Custom States</span>
    <md-content v-if="hasCustomStates" class="tags md-scrollbar">
      <table>
        <tr v-for="customState in customStates" :key="customState.id">
          <td class="state-cell">
            {{ customState.label }}
          </td>
          <td class="state-cell" valign="middle">
            <el-switch
              :value="customState.value"
              class="md-primary"
              on-text="True"
              on-color="#13ce66"
              off-text="False"
              off-color="#ff4949"
              :width="70"
              :disabled="readonly"
              @input="toggleCustomState(customState)"
            />
          </td>
          <td class="state-cell">
            <md-button
              :disabled="readonly"
              class="md-icon-button md-primary md-raised md-dense"
              @click="removeCustomState(customState)"
            >
              <md-icon>remove</md-icon>
            </md-button>
          </td>
        </tr>
      </table>
    </md-content>
    <div v-if="!hasCustomStates" class="nothing-here-message">
      Nothing in here yet
    </div>
    <el-form
      :inline="true"
      class="new-custom-state-form"
      @submit.native.prevent="addNewCustomState"
    >
      <el-form-item>
        <el-input
          class="new-custom-state-input"
          placeholder="Add a new custom state"
          :value="state.newCustomState.label"
          :disabled="readonly"
          @input="v => updateNewCustomState('label', v.trim())"
        />
      </el-form-item>
      <el-form-item>
        <el-select
          :value="state.newCustomState.value"
          :disabled="readonly"
          @input="v => updateNewCustomState('value', v)"
        >
          <el-option label="Is initially true" :value="true" />
          <el-option label="Is initally false" :value="false" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button
          type="success"
          icon="plus"
          :disabled="readonly"
          @click="addNewCustomState"
        />
      </el-form-item>
      <input type="submit" style="display: none;">
    </el-form>
    <md-dialog-alert
      :md-active.sync="state.conflict"
      md-title="Already exists"
      md-content="There is already a custom state with the name you entered."
    />
  </div>
</template>

<script>
import Chance from 'chance';
import { Action } from 'rete-history-plugin';
import cloneDeep from 'lodash/cloneDeep';
import without from 'lodash/without';

const chance = new Chance();

export default {
  name: 'custom-states-panel',
  props: {
    customStates: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      required: true,
    },
    emitter: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      state: {
        newCustomState: {
          label: '',
          value: true,
        },
        conflict: false,
      },
    };
  },
  computed: {
    hasCustomStates() {
      return this.customStates.length;
    },
  },
  watch: {
    customStates(newVal, oldVal) {
      if (this.disableHistoryTrackingOnNextProcess) {
        this.disableHistoryTrackingOnNextProcess = false;
        return;
      }
      this.emitter.trigger(
        'addhistory',
        new CustomStatesChangeAction(this, oldVal, newVal),
      );
    },
  },
  methods: {
    updateNewCustomState(key, value) {
      this.state.newCustomState[key] = value;
    },
    addNewCustomState() {
      if (!this.state.newCustomState.label) {
        return;
      }
      if (
        this.customStates.find(
          state => state.label === this.state.newCustomState.label,
        )
      ) {
        this.state.conflict = true;
        return;
      }
      this.state.newCustomState.id = chance.guid();
      this.$emit('update', this.customStates.concat(this.state.newCustomState));
      this.state.newCustomState = {
        label: '',
        value: true,
      };
    },
    toggleCustomState(customStateToToggle) {
      this.$emit(
        'update',
        this.customStates.map(customState => {
          if (customState === customStateToToggle) {
            return {
              ...customState,
              value: !customState.value,
            };
          }
          return customState;
        }),
      );
    },
    removeCustomState(customState) {
      this.$emit('update', without(this.customStates, customState));
    },
  },
};

class CustomStatesChangeAction extends Action {
  constructor(panelComponentInstance, oldCustomStates, newCustomStates) {
    super();
    this.panelComponentInstance = panelComponentInstance;
    this.oldCustomStates = cloneDeep(oldCustomStates);
    this.newCustomStates = cloneDeep(newCustomStates);
  }

  undo() {
    this.panelComponentInstance.disableHistoryTrackingOnNextProcess = true;
    this.panelComponentInstance.$emit('update', this.oldCustomStates);
  }

  redo() {
    this.panelComponentInstance.disableHistoryTrackingOnNextProcess = true;
    this.panelComponentInstance.$emit('update', this.newCustomStates);
  }
}
</script>

<style scoped>
.custom-states-panel {
  padding: 0 10px;
  max-width: 500px;
}

.tags {
  overflow: auto;
  height: calc(100% - 64px);
  background-color: #303143;
}

.state-cell {
  padding: 6px;
  color: white;
}

form.el-form.new-custom-state-form.el-form--inline {
  padding-top: 6px;
  height: 40px;
  display: flex;
}

.md-icon-button.md-dense {
  width: 24px;
  min-width: 24px;
  height: 24px;
}

.nothing-here-message {
  padding: 10px 0;
  color: lightgray;
}
</style>
